import { Percent, Plus, Edit2, Trash2, Loader2, Calendar, Users, Tag } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import { DiscountCodeForm } from './DiscountCodeForm';

interface DiscountCode {
  id: string;
  code: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  duration_type: string;
  duration_months: number | null;
  max_redemptions: number | null;
  expires_at: string | null;
  created_at: string | null;
  is_active: boolean;
}

interface DiscountCodeWizardProps {
  companyId: string;
  onComplete: () => void;
  onBack: () => void;
  showCompleteButton?: boolean;
}

type DiscountCodeView = 'list' | 'form';

export function DiscountCodeWizard({ companyId, onComplete, onBack, showCompleteButton = true }: DiscountCodeWizardProps) {
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<DiscountCodeView>('list');
  const [editingCode, setEditingCode] = useState<DiscountCode | null>(null);

  const loadDiscountCodes = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data: codes, error } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDiscountCodes(codes || []);
    } catch (err: unknown) {
      // Error loading discount codes
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadDiscountCodes();
    }
  }, [companyId, loadDiscountCodes]);

  const handleCreate = () => {
    setEditingCode(null);
    setCurrentView('form');
  };

  const handleEdit = (code: DiscountCode) => {
    setEditingCode(code);
    setCurrentView('form');
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;

    try {
      const { error } = await supabase
        .from('discount_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setDiscountCodes(prev => prev.filter(c => c.id !== id));
    } catch (err: unknown) {
      alert('Failed to delete discount code');
    }
  };

  const handleSave = (code: DiscountCode) => {
    if (editingCode) {
      setDiscountCodes(prev => prev.map(c => c.id === code.id ? code : c));
    } else {
      setDiscountCodes(prev => [code, ...prev]);
    }
    setCurrentView('list');
    setEditingCode(null);
  };

  const handleCancel = () => {
    setCurrentView('list');
    setEditingCode(null);
  };

  const formatDiscount = (code: DiscountCode) => {
    if (code.discount_type === 'percent') {
      return `${code.discount_value}% off`;
    }
    return `$${code.discount_value.toFixed(2)} off`;
  };

  const formatDuration = (code: DiscountCode) => {
    if (code.duration_type === 'forever') return 'Forever';
    if (code.duration_type === 'once') return 'First payment only';
    if (code.duration_type === 'repeating' && code.duration_months) {
      return `${code.duration_months} month${code.duration_months > 1 ? 's' : ''}`;
    }
    return code.duration_type;
  };

  const isExpired = (code: DiscountCode) => {
    if (!code.expires_at) return false;
    return new Date(code.expires_at) < new Date();
  };

  if (loading) {
    return (
      <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-rollops-accent" />
        </div>
      </div>
    );
  }

  if (currentView === 'form') {
    return (
      <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
        <DiscountCodeForm
          companyId={companyId}
          discountCode={editingCode}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <Percent size={32} className="text-rollops-accent" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Discount Codes</h2>
        <p className="text-[#A9A3A3] mt-2">Create promotional offers</p>
      </div>

      {discountCodes.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-20 h-20 rounded-full bg-[#363232] flex items-center justify-center mx-auto mb-4">
            <Percent size={40} className="text-[#504A4A]" />
          </div>
          <h3 className="text-xl font-semibold text-[#EBFFEE] mb-2">No Discount Codes Yet</h3>
          <p className="text-[#A9A3A3] mb-6">Create discount codes to offer promotions to your members</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-medium rounded-lg transition-colors"
          >
            <Plus size={18} />
            Create First Discount Code
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4 flex justify-end">
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-medium rounded-lg transition-colors"
            >
              <Plus size={18} />
              Add Code
            </button>
          </div>

          <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto">
            {discountCodes.map((code) => (
              <div
                key={code.id}
                className={`bg-[#363232] rounded-xl border p-4 transition-colors ${
                  code.is_active && !isExpired(code)
                    ? 'border-[#504A4A] hover:border-[#9B1421]/50'
                    : 'border-[#504A4A]/50 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Tag size={14} className="text-rollops-accent" />
                      <h3 className="font-mono font-bold text-[#EBFFEE]">{code.code}</h3>
                    </div>
                    {code.description && (
                      <p className="text-xs text-[#A9A3A3] mt-1">{code.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(code)}
                      className="p-2 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#504A4A] rounded-lg transition-colors"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(code.id)}
                      className="p-2 text-[#A9A3A3] hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    <Percent size={12} className="text-green-500" />
                    <span className="text-[#EBFFEE] font-medium">{formatDiscount(code)}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar size={12} className="text-[#A9A3A3]" />
                    <span className="text-[#A9A3A3]">{formatDuration(code)}</span>
                  </div>

                  {code.max_redemptions && (
                    <div className="flex items-center gap-2">
                      <Users size={12} className="text-[#A9A3A3]" />
                      <span className="text-[#A9A3A3]">Max {code.max_redemptions} uses</span>
                    </div>
                  )}

                  {code.expires_at && (
                    <div className={`text-xs ${isExpired(code) ? 'text-red-400' : 'text-[#A9A3A3]'}`}>
                      {isExpired(code) ? 'Expired' : `Expires: ${new Date(code.expires_at).toLocaleDateString()}`}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex gap-4 pt-4 border-t border-[#504A4A]">
        <button
          onClick={onBack}
          className="flex-1 py-3 px-4 border border-[#504A4A] hover:bg-[#363232] text-[#A9A3A3] font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        {showCompleteButton && (
          <button
            onClick={onComplete}
            className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
          >
            Complete Setup
          </button>
        )}
        {!showCompleteButton && (
          <button
            onClick={onComplete}
            className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
          >
            Done
          </button>
        )}
      </div>
    </div>
  );
}
